import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { type EventStreamUpdate, type IcsBlobItemResponse, type ProgressResponse, ProcessStatus, ProcessType, useNotificationsStore } from '@/store/notificationsStore';
import { useDatabaseSchemaStore } from '@/store/Setup/databaseSchemaStore.js';
import { useTableDefinitionsStore } from '@/store/tableDefinitionsStore';
import { useImportDefinitionStore } from '@/store/Setup/importDefinitionStore.js';
import { useUploadStoreV2 } from '@/store/storeUploadV2';
import { useFilePreviewStore } from '@/store/Setup/filePreviewStore';
import { useImportStore } from '@/store/storeImport';
import { useImportLogStore } from '@/store/importLogStore';
import type { ImportColumnMappingRequest, ImportJobRequest } from '@/types/ImportJobRequest';
import { saveTemplateApi } from '@/api/endpoints/import';
// Bruk auto-genererte typer fra Swagger
import type { components } from '@/api/types/auto-generated';

console.log('[Wizard] importWizardStore.ts initialiseres');

// Interfaces for wizard data
export interface Schema {
  name: string;
}

// Interface for den ytre strukturen (tabell-definisjonen)
export interface TableDefinition {
  systemObjectId: number;
  tableDefinitionId: number;
  tableName: string;
  tableSchemaName: string;
  systemObjectName: string;
  columns: TableColumnDefinition[];
}

// Interface for kolonne-definisjoner
export interface TableColumnDefinition {
  tableDefinitionId: number;
  dbColumnName: string;
  columnLabel: string;
  listOfValueObjectId: number | null;
  simpleListOfValue: string | null;
  simpleListOfValueSeparator: string | null;
  componentEditId: number;
  componentEdit: string;
  systemColumn: SystemColumnDefinition;
  isSystemColumn: boolean;
  isViewColumn: boolean;
  numberFormatShowThousandsSeparator: boolean | null;
  numberFormatDecimalPrecision: number | null;
  includeInputFormNew: boolean;
  isReadOnly: boolean;
}

// Interface for system kolonner (nested i kolonne-definisjonen)
export interface SystemColumnDefinition {
  columnName: string;
  isNullable: boolean;
  precision: number;
  scale: number;
  collationName: string | null;
  ordinal: number;
  isIdentityColumn: boolean;
  isPartOfUniqueKey: boolean;
  isUpdateable: boolean;
  isReadOnly: boolean;
  isSystemColumn: boolean;
  textWithMaxLength: boolean;
  columnDefault: string | null;
  systemTypeName: string;
  isRequired: boolean;
  maxLength: number;
  baseDataType: string;
}

export interface TableListItem {
  objectName: string;
  schemaName: string;
  objectType: string;
  objectTypeDescription: string;
  tableDefinitionId: number | null;
}

export interface ImportTemplate {
  importDefinitionId: number;
  name: string;
  description: string;
  tableDefinitionId: number;
  active: boolean;
  columns: ImportColumnDefinition[]; // Backend returnerer columns array
  steps?: Record<string, boolean>; // Backend returnerer steps som objekt (Axios parser JSON)
}

export type ImportColumnDefinition = components['schemas']['ImportColumnDefinition'];

export interface ColumnMapping {
  fileColumn: string;
  tableColumn: string;
  isKey: boolean;
  dateFormat?: string;
  isCommentColumn: boolean;
  nullValue?: string;
}

type SimulationActivityData = {
  name: string;
  count: number;
  description: string;
};

type SimulationActivityResult = {
  hasError: boolean;
  isSuccess: boolean;
  taskId: string;
  taskName: string;
  message: string | null;
  errors: string[];
  duration: number;
  status: string;
  continuationToken: any;
  data: SimulationActivityData[] | null;
};

export interface SimulationResponse {
  isComplete: boolean;
  hasError: boolean;
  activityResults: SimulationActivityResult[];
  totalTasks: number;
  errorTasks: number;
  errorMessages: string[];
  duration: number;
  data: SimulationActivityData[];
  hasAggregateError: boolean;
  startTime: string;
  endTime: string;
  processType: string;
}

// Endre simulationResult til å bruke SimulationResponse
type SimulationResultType = SimulationResponse | null;
ref<SimulationResultType>(null);

export interface AnalyseCol {
  sourceColumnName: string;
  tableColumnName: string;
  mappingStatus: string;
  possiblyKey?: boolean;
}

// Intern type for mapping med selected og key
export interface ColumnMappingWithType extends ColumnMapping {
  selected: boolean;
  key: number;
}

export interface WizardStepConfig {
  label: string;
  title: string;
  description: string;
  canProceed: () => boolean;
  onClick: () => Promise<void> | void;
  icon?: string;
  isTemplateStep?: boolean; // NY: For å håndtere spesielle knapper på step 6
}

// Ny enum for template-valg
export type TemplateChoice = 'existing' | 'new' | 'manual';

export const useImportWizardStore = defineStore('importWizard', () => {
  const notificationsStore = useNotificationsStore();
  const databaseSchemaStore = useDatabaseSchemaStore();
  const tableDefinitionsStore = useTableDefinitionsStore();
  const importDefinitionStore = useImportDefinitionStore();
  const uploadStore = useUploadStoreV2();
  const filePreviewStore = useFilePreviewStore();
  const importStore = useImportStore();
  const importLogStore = useImportLogStore();

  const { tables: tableDefinitions } = storeToRefs(tableDefinitionsStore);
  const { preview, analyse } = storeToRefs(filePreviewStore);
  const { schemas, tables } = storeToRefs(databaseSchemaStore);
  const { importDefinitions } = storeToRefs(importDefinitionStore);
  const { getFileByUploadId, getFilesByExtractedFromUploadId } = useUploadStoreV2();

  // State
  const isLoadingSchemas = ref(false);
  const isLoadingTables = ref(false);
  const isLoadingTemplates = ref(false);
  const isSimulating = ref(false);
  const isAnalyzing = ref(false);
  const isImporting = ref(false);
  const isUploadingFile = ref(false);
  const isUploadProcessing = ref(false);
  const isWorking = computed(() => isSimulating.value || isAnalyzing.value || isImporting.value);


  const currentStep = ref(1);
  const totalSteps = ref(7);
  const selectedSchema = ref<string>('');
  const selectedTableDefinitionId = ref<number | null>(null);
  const selectedTableName = ref<string>('');
  const selectedUploadId = ref<string>('');
  const activeUploadId = ref<string>('');
  const useExistingTemplate = ref<TemplateChoice | null>(null);
  const selectedTemplate = ref<string | null>(null);
  const newTemplateName = ref<string>('');
  const newTemplateDescription = ref<string>('');
  const importTemplates = ref<ImportTemplate[]>([]);
  const simulationResult = ref<SimulationResultType>(null);
  const importResult = ref<SimulationResultType>(null);
  const currentImportId = ref<string | null>(null);
  const headerRowIndex = ref(1);
  const firstDataRowIndex = ref(2);
  const previewLines = ref(5);
  const analyseLines = ref(100);
  const analyseError = ref<string | null>(null);
  const uploadProgress = ref(0);


  const eventStreamLogs = ref<EventStreamUpdate[]>([]);
  const eventStreamProgress = ref<Record<string, EventStreamUpdate>>({});

  // Step 1: Table selection
  const currentTable = ref<TableDefinition | null>(null);

  const selectedImportActivities = ref<string[]>([]);
  const previewColumns = ref<string[]>([]);
  const previewRows = ref<any[]>([]);
  const emailReceipt = ref(false);

  // NY: Mapping av simuleringsresultat til relevante aktiviteter
  // Bruker PascalCase nøkler som matcher backend FunctionNames
  const getRelevantActivitiesForSimulation = computed(() => {
    if (!simulationResult.value?.data) return [];

    const activities: string[] = [];
    const counters = getSimulationCounters();

    // Basert på simuleringsresultat, velg relevante aktiviteter
    if (counters['New row'] > 0) {
      activities.push('copyToTarget_Activity'); // Legg til nye rader
    }

    if (counters['Changed'] > 0) {
      activities.push('updateTarget_Activity'); // Oppdater eksisterende rader
    }

    if (counters['Missing'] > 0) {
      activities.push('softDeleteInTarget_Activity'); // Slett rader som mangler i fil
    }

    if (counters['Restore'] > 0) {
      activities.push('softRestoreInTarget_Activity'); // Gjenopprett rader som finnes i fil igjen
    }

    // Kommentarer kan alltid være relevant
    activities.push('updateTargetComments_Activity');

    return activities;
  });

  // NY: Automatisk velg aktiviteter basert på simuleringsresultat
  const autoSelectActivities = () => {
    selectedImportActivities.value = getRelevantActivitiesForSimulation.value;
  };

  // NY: Last steps fra valgt mal
  const loadStepsFromTemplate = async (template?: ImportTemplate | null) => {
    console.log('[Wizard] loadStepsFromTemplate called');

    // Tøm eksisterende mappings først
    allMappings.value = [];
    selectedImportActivities.value = [];

    const templateToUse = template || currentTemplate.value;

    if (templateToUse?.steps) {
      // Steps er allerede et objekt (Axios parser JSON)
      const templateSteps = { ...templateToUse.steps };
      console.log('[Wizard] loadStepsFromTemplate: Template steps:', templateSteps);

      // Hent gyldige selectable activity keys fra importStore
      const importStore = useImportStore();

      // Sørg for at aktiviteter er lastet før vi filtrerer
      if (!importStore.importActivities) {
        console.log('[Wizard] loadStepsFromTemplate: Aktiviteter ikke lastet, henter fra backend...');
        await importStore.fetchImportActivities();
      }

      const selectableKeys = importStore.selectableActivityKeys;

      // Advarsel hvis aktiviteter fortsatt mangler (burde ikke skje)
      if (selectableKeys.length === 0) {
        console.warn('[Wizard] loadStepsFromTemplate: Ingen selectable aktiviteter tilgjengelig! Template steps vil ikke bli lastet.');
        return;
      }

      // Normaliser template steps keys til camelCase (håndterer både PascalCase og camelCase fra DB)
      const normalizedSelectableKeys = new Set(selectableKeys.map((k: string) => k.toLowerCase()));

      // Filtrer steps som er aktivert og er gyldige selectable activities (case-insensitive matching)
      const filteredActivities = Object.entries(templateSteps)
        .filter(([key, isSelected]) => {
          if (!isSelected) return false;
          // Case-insensitive match mot selectable keys
          const normalizedKey = key.toLowerCase();
          if (normalizedSelectableKeys.has(normalizedKey)) {
            // Finn den korrekte camelCase keyen fra selectableKeys
            const correctKey = selectableKeys.find((k: string) => k.toLowerCase() === normalizedKey);
            return correctKey !== undefined;
          }
          return false;
        })
        .map(([key]) => {
          // Returner korrekt camelCase key fra selectableKeys
          const normalizedKey = key.toLowerCase();
          const correctKey = selectableKeys.find((k: string) => k.toLowerCase() === normalizedKey);
          return correctKey || key; // Fallback til original key hvis ikke funnet (burde ikke skje)
        });

      selectedImportActivities.value = filteredActivities;
    }

    // Last mappings fra columns array
    if (templateToUse?.columns) {
      console.log('[Wizard] loadStepsFromTemplate: Loading mappings from columns:', templateToUse.columns);
      for (const column of templateToUse.columns) {
        console.log('[Wizard] Processing column:', column);
        console.log('[Wizard] Column isKey:', column.isKey, 'type:', typeof column.isKey);
        console.log('[Wizard] Column isCommentColumn:', column.isCommentColumn, 'type:', typeof column.isCommentColumn);

        // Valider at nødvendige felter er tilgjengelige
        if (!column.sourceColumnName || !column.dbColumnName) {
          console.warn('[Wizard] Skipping column with missing sourceColumnName or dbColumnName:', column);
          continue;
        }

        const mapping: ColumnMapping = {
          fileColumn: column.sourceColumnName,
          tableColumn: column.dbColumnName, // Backend returnerer dbColumnName fra ImportColumnDefinition
          isKey: column.isKey === true, // Backend bruker 'isKey' (boolean)
          dateFormat: column.dateFormat || 'ymd',
          isCommentColumn: column.isCommentColumn === true, // Backend bruker 'isCommentColumn' (boolean)
          nullValue: column.nullValue || ''
        };

        if (mapping.isKey) {
          addKeyMapping(mapping);
        } else if (mapping.isCommentColumn) {
          addCommentMapping(mapping);
        } else {
          addValueMapping(mapping);
        }
      }
      console.log('[Wizard] loadStepsFromTemplate: Loaded mappings from columns');
    }
  };

  // NY: Toggle aktivitet
  const toggleImportActivity = (activity: string) => {
    const index = selectedImportActivities.value.indexOf(activity);
    if (index > -1) {
      selectedImportActivities.value.splice(index, 1);
    } else {
      selectedImportActivities.value.push(activity);
    }
  };

  // NY: Sjekk om aktivitet er valgt
  const isImportActivitySelected = (activity: string): boolean => {
    return selectedImportActivities.value.includes(activity);
  };

  // NY: Hjelpefunksjoner for kolonne-typer
  const isDateColumn = (columnName: string): boolean => {
    return ['date', 'datetime', 'datetime2'].includes(getColumnDataType(columnName).toLowerCase() || '');
  };

  const isCommentColumn = (columnName: string): boolean => {
    return getColumn(columnName)?.componentEdit === 'editComment' || false;
  };

  function getColumnDataType (columnName: string): string {
    return getColumn(columnName)?.systemColumn.baseDataType || getColumn(columnName)?.systemColumn.systemTypeName || 'ukjent';
  }

  function getColumn (columnName: string): TableColumnDefinition | null {
    if (!currentTable.value?.columns || !columnName) return null;
    const column = currentTable.value?.columns.find((c: TableColumnDefinition) => c.dbColumnName === columnName);
    return column || null;
  }

  function isKeyColumn (tableColumn: string): boolean {
    return keyMappings.value.some(m => m.tableColumn === tableColumn);
  }

  const sheetName = computed(() => {
    if (!selectedUploadId.value) return '';
    const sheet = sheets.value.find((s: any) => s.uploadId === selectedUploadId.value);
    return sheet?.sheetName || '';
  });

  const currentTemplate = computed(() => {
    return importTemplates.value.find((t: ImportTemplate) => t.importDefinitionId === Number(selectedTemplate.value)) || null;
  });

  const uploadedFile = computed(() => {
    if (!activeUploadId.value) return null;
    return getFileByUploadId(activeUploadId.value);
  });

  const sheets = computed(() => {
    if (!activeUploadId.value) return [];
    return getFilesByExtractedFromUploadId(activeUploadId.value);
  });

  // Enkel polling-funksjon for simulering-status
  async function setSchema (val: string) {
    console.log('[Wizard] setSchema:', val);
    await setTable(null);
    selectedSchema.value = val;
    if (selectedSchema.value) {
      await fetchTablesForSchema(selectedSchema.value);
    }
  }

  async function setTable (val: number | null) {
    console.log('[Wizard] setTable:', val, selectedTableDefinitionId.value, currentTable.value?.tableDefinitionId);
    if (val === null) return;

    selectedTableDefinitionId.value = val;
    await tableDefinitionsStore.fetchByDefinitionId(val);
    currentTable.value = tableDefinitions.value[val];

    // Sett selectedTableName basert på schema og tableName
    if (currentTable.value) {
      selectedTableName.value = `${currentTable.value.tableSchemaName}.${currentTable.value.tableName}`;
    } else {
      selectedTableName.value = '';
    }

    console.log('[Wizard] setTable: table:', currentTable.value);
    await fetchAndSyncImportTemplates();
  }

  function setTemplateChoice (choice: TemplateChoice | null) {
    console.log('[Wizard] setTemplateChoice:', choice);
    if (choice !== null && useExistingTemplate.value !== choice) {
      useExistingTemplate.value = choice;
    }

    // Nullstill alt når man bytter mellom valg
    selectedTemplate.value = '';
    newTemplateName.value = '';
    newTemplateDescription.value = '';
    allMappings.value = [];
    selectedImportActivities.value = [];
  }

  // NY: Sett valgt mal og last steps
  async function setSelectedTemplate (templateId: string | null) {
    console.log('[Wizard] setSelectedTemplate called with:', templateId);
    selectedTemplate.value = templateId;
    if (templateId) {
      const template = importTemplates.value.find((t: ImportTemplate) => t.importDefinitionId === Number(templateId));
      if (template) {
        console.log('[Wizard] setSelectedTemplate: Loading steps for template:', template);
        await loadStepsFromTemplate(template);
      } else {
        console.log('[Wizard] setSelectedTemplate: No template found for templateId:', templateId);
      }
    }
  }

  async function fileInputChanged (val: string) {
    console.log('[Wizard] fileInputChange:', val);
    if (val) {
      selectedUploadId.value = val;
      populatePreviewData();
    }
  }

  // Actions
  async function fetchSchemas () {
    isLoadingSchemas.value = true;
    try {
      await databaseSchemaStore.fetchSchemas();
    } catch (error) {
      console.error('Feil ved henting av liste med database-schema:', error);
      notificationsStore.addError('Kunne ikke hente liste med database-schema');
    } finally {
      isLoadingSchemas.value = false;
    }
  }

  async function fetchTablesForSchema (schemaName: string) {
    isLoadingTables.value = true;
    try {
      await databaseSchemaStore.fetchTables(schemaName);
    } catch (error) {
      console.error('Feil ved henting av tabeller:', error);
      notificationsStore.addError('Kunne ikke hente tabeller');
    } finally {
      isLoadingTables.value = false;
    }
  }

  async function processFile (file: File) {
    isUploadingFile.value = true;
    isUploadProcessing.value = true;
    uploadProgress.value = 0;
    let uploadId: string | null = null;

    try {
      const uploadIds = await uploadStore.postFiles([file], fileProgress);
      if (!uploadIds || !Array.isArray(uploadIds) || uploadIds.length === 0) {
        console.error('[Wizard] processFile: Ingen uploadId-er returnert');
        notificationsStore.addError('Kunne ikke laste opp fil');
        isUploadProcessing.value = false;
        return;
      }
      uploadId = uploadIds[0];
      if (uploadId) {
        activeUploadId.value = uploadId;
      }
    } catch (err) {
      console.error('[Wizard] processFile: Feil ved opplasting', err);
      notificationsStore.addError('Kunne ikke laste opp fil');
      isUploadProcessing.value = false;
      return;
    } finally {
      console.log('[Wizard] processFile done. uploadId:', activeUploadId.value);
      isUploadingFile.value = false;
    }

    // Timeout/fallback: Hvis SignalR-meldinger ikke kommer innen 30 sekunder, sjekk fil-status
    if (uploadId) {
      setTimeout(async () => {
        // Sjekk om vi fortsatt venter på SignalR-meldinger
        if (isUploadProcessing.value && activeUploadId.value === uploadId) {
          console.log('[Wizard] Timeout: Ingen SignalR-meldinger mottatt innen 30 sekunder, sjekker fil-status...');
          try {
            await uploadStore.fetchFileStatus(uploadId);
            const file = getFileByUploadId(uploadId);

            if (file) {
              const isComplete = !file.isProcessing && !file.isFailed && (file.processedDate || file.isReadyForImport);

              if (isComplete) {
                console.log('[Wizard] Fil er ferdig behandlet (timeout fallback), henter preview...');
                isUploadingFile.value = false;
                await fetchPreviewForCurrentFile(uploadId);
                isUploadProcessing.value = false;
              } else if (file.isFailed) {
                console.log('[Wizard] Fil har feilet (timeout fallback)');
                isUploadingFile.value = false;
                isUploadProcessing.value = false;
                previewError.value = file.error || 'Filen kunne ikke behandles';
              } else {
                // Filen behandles fortsatt, men SignalR-meldinger når ikke frem
                console.warn('[Wizard] Fil behandles fortsatt, men SignalR-meldinger når ikke frem. Prøver å synkronisere...');
                // Prøv å synkronisere en gang til
                await syncFileStatusOnConnection();
              }
            }
          } catch (error) {
            console.error('[Wizard] Feil ved timeout fallback:', error);
            // Ikke sett isUploadProcessing til false her - la brukeren prøve å laste siden på nytt
          }
        }
      }, 30000); // 30 sekunder
    }
  }

  function fileProgress (progressValue: number) {
    uploadProgress.value = progressValue;
    console.log('[Wizard progressUpdate] progress:', progressValue);
  }

  // Hjelpefunksjon for å bygge komplett columnMappings-payload
  function buildSteps (): Record<string, boolean> {
    const steps: Record<string, boolean> = {};
    for (const activity of selectedImportActivities.value) {
      steps[activity] = true;
    }
    return steps;
  }

  function getOrdinal (columnName: string): number {
    const orderInFile = Object.entries(preview.value?.headerRow ?? {}).find(item => item[1] === columnName);
    return orderInFile ? Number(orderInFile[0]) : 0;
  }

  function buildColumnMappings (): ImportColumnMappingRequest[] {

    return selectedAndKeyMappings.value.map(mapping => ({
      targetColumnName: mapping.tableColumn,
      sourceColumnName: mapping.fileColumn,
      isKey: mapping.isKey,
      dateFormat: mapping.dateFormat || 'ymd',
      isCommentColumn: mapping.isCommentColumn,
      nullValue: mapping.nullValue || '',
      ordinal: getOrdinal(mapping.fileColumn),
    }));
  }


  // NY: Datoformat-options
  const dateFormatOptions = ref([
    { value: 'ymd', label: 'År, måned og dag (YYYY-MM-DD)' },
    { value: 'dmy', label: 'Dag, måned og år (DD-MM-YYYY)' },
    { value: 'mdy', label: 'Måned, dag og år (MM-DD-YYYY)' },
  ]);


  // Intern samlet mapping-state
  const allMappings = ref<ColumnMappingWithType[]>([]);

  const keyMappings = computed(() => allMappings.value.filter(m => m.isKey));
  const valueMappings = computed(() => allMappings.value.filter(m => !m.isKey && !m.isCommentColumn));
  const commentMappings = computed(() => allMappings.value.filter(m => m.isCommentColumn));
  const selectedValueMappings = computed(() => valueMappings.value.filter(m => m.selected));
  const selectedCommentMappings = computed(() => commentMappings.value.filter(m => m.selected));
  const selectedAndKeyMappings = computed(() => allMappings.value.filter(m => m.selected || m.isKey));
  const selectedMappings = computed(() => allMappings.value.filter(m => m.selected && !m.isKey));
  const selectableMappings = computed(() => allMappings.value.filter(m => !m.isKey));

  const findMapping = (mapping: ColumnMapping) => allMappings.value.find(m => m.fileColumn === mapping.fileColumn);
  const isMappingSelected = (mapping: ColumnMapping) => findMapping(mapping)?.selected ?? false;

  function addMapping (mapping: ColumnMapping, mapType: 'key' | 'value' | 'comment') {
    if (findMapping(mapping)) {
      notificationsStore.addError('Kolonne ' + mapping.tableColumn + ' finnes allerede i mapping');
      return;
    }

    switch (mapType) {
      case 'key':
        mapping.isKey = true;
        mapping.isCommentColumn = false;
        break;
      case 'value':
        mapping.isKey = false;
        mapping.isCommentColumn = false;
        if (isDateColumn(mapping.tableColumn) && !mapping.dateFormat) {
          notificationsStore.addError('Kolonne ' + mapping.tableColumn + ' er en dato-kolonne, men har ikke et datoformat valgt');
          return;
        }
        break;
      case 'comment':
        mapping.isKey = false;
        mapping.isCommentColumn = true;
        break;
    }
    allMappings.value.push({ ...mapping, selected: true, key: allMappings.value.length });
  }

  const addValueMapping = (mapping: ColumnMapping) => addMapping(mapping, 'value');
  const addKeyMapping = (mapping: ColumnMapping) => addMapping(mapping, 'key');
  const addCommentMapping = (mapping: ColumnMapping) => addMapping(mapping, 'comment');

  function removeMapping (mapping: ColumnMapping) {
    const idx = allMappings.value.findIndex(m => m.fileColumn === mapping.fileColumn);
    if (idx !== -1) allMappings.value.splice(idx, 1);
  }

  const removeValueMapping = (mapping: ColumnMapping) => removeMapping(mapping);
  const removeKeyMapping = (mapping: ColumnMapping) => removeMapping(mapping);
  const removeCommentMapping = (mapping: ColumnMapping) => removeMapping(mapping);

  function toggleMappingSelection (mapping: ColumnMapping) {
    const m = findMapping(mapping);
    if (m) m.selected = !m.selected;
  }

  function updateMapping (mappingKey: number, field: 'fileColumn' | 'tableColumn' | 'dateFormat' | 'nullValue', value: string) {
    const index = allMappings.value.findIndex(m => m.key === mappingKey);
    if (index !== -1 && allMappings.value[index]) {
      allMappings.value[index][field] = value;
    }
  }

  // Generisk funksjon for å hente resultat (både import og simulering)
  async function fetchResult (importId: string, retryCount: number = 0): Promise<any> {
    const delays = [500, 1000, 5000]; // 500ms, 1s, 5s
    const delay = delays[retryCount];

    try {
      currentImportId.value = importId;
      const result = await importLogStore.fetchImportLogByImportId(importId);

      if (result?.isComplete || result?.hasError) {
        console.log('[Wizard] fetchResult response:', result);
        return result;
      }

      // Resultatet er ikke klart ennå, prøv igjen
      if (retryCount < 3) {
        console.log(`[Wizard] Resultat ikke klart ennå, prøver igjen om ${delay}ms (forsøk ${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchResult(importId, retryCount + 1);
      }

      // Etter 3 forsøk, gi opp
      console.error('[Wizard] Klarte ikke hente resultat etter 3 forsøk');
      notificationsStore.addError('Klarte ikke hente resultat fra import/simulering', 'Se link til rapporten for detaljer');
      return null;

    } catch (error) {
      console.error('[Wizard] Feil ved henting av resultat:', error);

      // Prøv igjen ved feil
      if (retryCount < 3) {
        console.log(`[Wizard] Feil ved henting, prøver igjen om ${delay}ms (forsøk ${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchResult(importId, retryCount + 1);
      }

      // Etter 3 forsøk, gi opp
      console.error('[Wizard] Klarte ikke hente resultat etter 3 forsøk (feil)');
      notificationsStore.addError('Klarte ikke hente resultat fra import/simulering', 'Se link til rapporten for detaljer');
      return null;
    }
  }

  type CounterMap = { [key: string]: number };

  function getSimulationCounters (): CounterMap {
    const result = simulationResult.value;
    if (!result?.data) return {};
    const counters: CounterMap = {};
    for (const entry of result.data) {
      counters[entry.name] = entry.count;
    }
    return counters;
  }

  function getSimulationCountByName (name: string): number {
    const result = simulationResult.value;
    if (!result?.data) return 0;
    const found = result.data.find(d => d.name === name);
    return found ? found.count : 0;
  }

  // Flytt opp analyseFileForSuggestions og handleSimulationComplete før startImportOrSimulation
  async function analyseFileForSuggestions () {
    // Kall analyseFile fra filePreviewStore med riktige parametre
    if (!selectedTableDefinitionId.value) throw new Error('Mangler tableDefinitionId for analyse');

    // Hvis brukeren har valgt en eksisterende mal, bruk den og ikke la analysen overskrive
    let importDefinitionId = null;
    if (useExistingTemplate.value === 'existing' && selectedTemplate.value) {
      importDefinitionId = selectedTemplate.value;
      console.log('[Wizard] analyseFileForSuggestions: Bruker valgt mal fra steg 2:', importDefinitionId);
    }

    const ok = await filePreviewStore.analyseFile(selectedTableDefinitionId.value, importDefinitionId);
    if (!ok) throw new Error('Analyse av filen feilet');

    // Hvis brukeren har valgt en eksisterende mal, sørg for at mappingen ikke overskrives
    if (useExistingTemplate.value === 'existing' && selectedTemplate.value && currentTemplate.value) {
      console.log('[Wizard] analyseFileForSuggestions: Gjenoppretter mapping fra valgt mal etter analyse');
      // Last mappingen fra den valgte malen på nytt for å sikre at den ikke er overskrevet
      await loadStepsFromTemplate();
    } else if (useExistingTemplate.value === 'new' || useExistingTemplate.value === 'manual') {
      console.log('[Wizard] analyseFileForSuggestions: Bruker analyse-resultat for ny/manuell import');
      // For ny eller manuell import, bruk analysen for å foreslå mapping
      // Dette er allerede håndtert av analysen
    }
  }

  async function fetchAndSyncImportTemplates () {
    if (!selectedTableDefinitionId.value) {
      console.warn('[Wizard] fetchAndSyncImportTemplates: ingen valgt tabell');
      return;
    }

    await importDefinitionStore.fetchImportDefinitions(selectedTableDefinitionId.value);

    importTemplates.value = (importDefinitions.value || []).map((def: any) => ({
      importDefinitionId: def.importDefinitionId,
      name: def.name || def.description, // Bruk name hvis tilgjengelig, ellers description
      description: def.description,
      tableDefinitionId: selectedTableDefinitionId.value,
      active: def.active,
      columns: def.columns || [], // Backend returnerer columns array
      steps: def.steps || {}, // Backend returnerer steps som objekt (Axios parser JSON)
    }));

    // Sorter etter nyeste først (høyeste ImportDefinitionId)
    importTemplates.value.sort((a, b) => b.importDefinitionId - a.importDefinitionId);

    // Sett default basert på om det finnes maler
    if (importTemplates.value.length > 0) {
      // Hvis det finnes maler, sett default til 'existing' og velg den nyeste
      if (useExistingTemplate.value === null) {
        useExistingTemplate.value = 'existing';
        await setSelectedTemplate(String(importTemplates.value[0].importDefinitionId));
      }
    } else {
      // Hvis ingen maler finnes, sett default til 'new'
      useExistingTemplate.value ??= 'new';
    }
  }


  // Generisk funksjon for både simulering og import
  async function startImportOrSimulation (importType: 'Import' | 'ImportSimulation') {
    if (importType === 'Import' && selectedImportActivities.value.length === 0) {
      notificationsStore.addError('Du må velge minst én handling å utføre');
      isImporting.value = false;
      return;
    }
    const uploadId = selectedUploadId.value;
    const importDefinitionId = selectedTemplate.value || null;
    const fileName = uploadedFile.value?.fileName || '';

    // Sjekk at vi har en importDefinitionId for simulering/import
    if (!importDefinitionId) {
      console.error('[Wizard] startImportOrSimulation: Mangler importDefinitionId');
      notificationsStore.addError('Mangler import-definisjon. Prøv å lagre malen på nytt.');
      return;
    }

    // Bygg payload for import endepunkt - kun de 4 feltene som backend forventer
    const payload: ImportJobRequest = {
      importDefinitionId: Number(importDefinitionId),
      importType: importType,
      debugOn: false,
      emailReceipt: emailReceipt.value
    };

    const result = await importStore.startImport(uploadId, importDefinitionId, importType, fileName, false, selectedImportActivities.value, payload);

    // Oppdater selectedTemplate hvis en ny import-definisjon ble opprettet eller hvis vi bruker en eksisterende
    if (result?.ImportDefinitionId) {
      selectedTemplate.value = String(result.ImportDefinitionId);
      console.log('[Wizard] Import-definisjon satt, selectedTemplate oppdatert til:', result.ImportDefinitionId);

      // Oppdater import-templates hvis en ny ble opprettet
      if (!importDefinitionId) {
        await fetchAndSyncImportTemplates()
      }
    }
  }

  // runSimulation og executeImport bruker nå kun startImportOrSimulation
  async function runSimulation () {
    isSimulating.value = true;
    console.log('[Wizard] runSimulation kalles, currentStep:', currentStep.value);

    // For nye maler og manuell import, lagre kladd før simulering
    if (useExistingTemplate.value === 'new' || useExistingTemplate.value === 'manual') {
      const draftSaved = await saveDraft();
      if (!draftSaved) {
        isSimulating.value = false;
        return;
      }

      // Sjekk at vi har en importDefinitionId etter at kladden er lagret
      if (!selectedTemplate.value) {
        console.error('[Wizard] runSimulation: Mangler importDefinitionId etter lagring av kladd');
        notificationsStore.addError('Kunne ikke lagre import-definisjon. Prøv igjen.');
        isSimulating.value = false;
        return;
      }
    }

    await startImportOrSimulation('ImportSimulation');
    goToStep(6);
    console.log('[Wizard] runSimulation startet, currentStep:', currentStep.value);
  }

  async function executeImport () {
    isImporting.value = true;
    console.log('[Wizard] executeImport kalles, currentStep:', currentStep.value);

    // Sjekk at vi har en importDefinitionId før import
    if (!selectedTemplate.value) {
      console.error('[Wizard] executeImport: Mangler importDefinitionId');
      notificationsStore.addError('Mangler import-definisjon. Prøv å lagre malen på nytt.');
      isImporting.value = false;
      return;
    }

    await startImportOrSimulation('Import');
    goToStep(7);
    console.log('[Wizard] executeImport startet, currentStep:', currentStep.value);
  }

  // Hjelpefunksjoner for saveTemplate
  function validateTemplateSave (): boolean {
    if (useExistingTemplate.value === 'new' || useExistingTemplate.value === 'manual') {
      return true;
    }
    console.error('[Wizard] saveTemplate kalles for eksisterende mal:', useExistingTemplate.value);
    return false;
  }

  function buildTemplateRequest (isDraft: boolean) {
    const isNewTemplate = useExistingTemplate.value === 'new';
    return {
      importDefinitionId: isDraft ? 0 : Number(selectedTemplate.value),
      name: isNewTemplate ? newTemplateName.value : 'Manuell import',
      description: isNewTemplate ? newTemplateDescription.value : 'Template laget for ad-hoc-import (ikke aktiv)',
      steps: isDraft ? {} : buildSteps(), // Tom steps for kladd, valgte steps for template
      isActive: isDraft ? false : isNewTemplate, // false for kladd, true for ny mal, false for manuell
      tableName: currentTable.value?.tableName || '',
      tableSchemaName: currentTable.value?.tableSchemaName || '',
      fileReaderOptions: filePreviewStore.getMetadata(),
      columns: buildColumnMappings().map(mapping => ({
        ordinal: mapping.ordinal,
        targetColumnName: mapping.targetColumnName,
        sourceColumnName: mapping.sourceColumnName,
        nullValue: mapping.nullValue,
        dateFormat: mapping.dateFormat,
        isKey: mapping.isKey,
        isCommentColumn: mapping.isCommentColumn
      })),
      importType: 'ImportSimulation' // Default import type for templates
    };
  }

  function handleTemplateSaveResponse (response: any, isDraft: boolean): void {
    if (response?.importDefinitionId) {
      selectedTemplate.value = String(response.importDefinitionId);
      console.log('[Wizard] saveTemplate: selectedTemplate oppdatert til:', response.importDefinitionId);
    }

    if (!isDraft) {
      const isNewTemplate = useExistingTemplate.value === 'new';
      const action = isNewTemplate ? `Importmal '${newTemplateName.value}' er lagret` : 'Import klar';
      notificationsStore.addSuccess(action);
    }
  }

  function handleTemplateSaveError (error: any, isDraft: boolean): void {
    console.error('[Wizard] Feil ved lagring:', error);
    const action = isDraft ? 'kladd' : 'mal';
    notificationsStore.addError(`Kunne ikke lagre ${action}. Prøv igjen senere.`);
  }

  // Lagre template/kladd med validering og API-kall
  async function saveTemplate (isDraft: boolean = false): Promise<boolean> {
    console.log('[Wizard] saveTemplate kalles, isDraft:', isDraft);

    if (!validateTemplateSave()) {
      if (!isDraft) {
        notificationsStore.addError('Kan kun lagre nye maler eller manuell import');
      }
      return false;
    }

    try {
      const request = buildTemplateRequest(isDraft);
      const response = await saveTemplateApi(request);
      handleTemplateSaveResponse(response, isDraft);
    } catch (error: any) {
      handleTemplateSaveError(error, isDraft);
      return false;
    }

    if (!isDraft) {
      await fetchAndSyncImportTemplates();
    }

    return true;
  }

  // Lagre kladd (draft) før simulering
  async function saveDraft (): Promise<boolean> {
    return saveTemplate(true);
  }

  async function saveTemplateAndExit () {
    if (await saveTemplate(false)) {
      await resetWizard();
    }
  }

  async function saveTemplateAndImport () {
    if (await saveTemplate(false)) {
      // Sjekk at vi har en importDefinitionId etter at malen er lagret
      if (!selectedTemplate.value) {
        console.error('[Wizard] saveTemplateAndImport: Mangler importDefinitionId etter lagring av mal');
        notificationsStore.addError('Kunne ikke lagre mal. Prøv igjen.');
        return;
      }
      await executeImport();
    }
  }

  // Spesialhåndtering for steg 3 → 4
  async function executeAnalyze () {
    console.log('[Wizard] executeAnalyze kalles, currentStep:', currentStep.value);
    isAnalyzing.value = true;
    analyseError.value = null;
    try {
      console.log('[Wizard] Kaller analyseFileForSuggestions');
      await analyseFileForSuggestions();
      isAnalyzing.value = false;
      goToStep(4);
    } catch (error: any) {
      isAnalyzing.value = false;
      analyseError.value = error?.message || 'Feil ved analyse av filen';
      console.error('[Wizard] Feil i analyse:', error);
    }
  }

  async function sendEmailReportForImport (importId?: string) {
    console.log('sendEmailReportForImport: called');
    const idToUse = importId || currentImportId.value;
    if (!idToUse) {
      console.error('sendEmailReportForImport: importId is missing');
      return;
    }
    const { sendEmailReport } = await import('@/api/endpoints/email')
    console.log('sendEmailReportForImport: about to call API with importId:', idToUse);
    try {
      await sendEmailReport(idToUse)
      notificationsStore.addSuccess('E-post sendt!')
    } catch (error) {
      console.error('Feil ved sending av e-post:', error)
      notificationsStore.addError('Kunne ikke sende e-post. Prøv igjen senere.')
    }
  }

  async function handleColumnMappingNext () {
    console.log('[Wizard] handleColumnMappingNext kalles');

    const validation = validateMappings();
    console.log('[Wizard] Validering resultat:', validation);
    console.log('[Wizard] Key mappings:', keyMappings.value);
    console.log('[Wizard] Value mappings:', valueMappings.value);
    console.log('[Wizard] Comment mappings:', commentMappings.value);

    if (!validation.isValid) {
      // Bruk faktiske feilmeldinger fra validering
      const errorMessage = validation.errors.length > 0
        ? validation.errors.join('; ')
        : 'Validering feilet';

      notificationsStore.addError('Kan ikke gå videre', errorMessage);
      console.log('[Wizard] Validering feilet, går ikke videre');
      return; // Ikke gå videre hvis validering feiler
    }

    console.log('[Wizard] Validering OK, går til step 5');
    goToStep(5);
  }

  // Navigation
  async function nextStep () {
    console.log('[Wizard] nextStep kalles, currentStep:', currentStep.value);
    if (currentStep.value < totalSteps.value) {
      currentStep.value++;
      console.log('[Wizard] currentStep økt til', currentStep.value);
    }
  }

  function previousStep () {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  }

  function goToStep (step: number) {
    if (step >= 1 && step <= totalSteps.value) {
      currentStep.value = step;
    }
  }

  // Oppdater resetFileOnly for å nullstille status
  function resetFileOnly () {
    console.log('[Wizard] resetFileOnly: nullstiller fil-relatert state');
    clearPreview();
    // Nullstill progress for forrige fil
    if (activeUploadId.value && eventStreamProgress.value[activeUploadId.value]) {
      delete eventStreamProgress.value[activeUploadId.value];
    }
    activeUploadId.value = '';
    selectedUploadId.value = '';
    uploadProgress.value = 0;
    isUploadingFile.value = false;
    isUploadProcessing.value = false;
    previewColumns.value = [];
    previewRows.value = [];
    isFetchingPreview.value = false;
    previewError.value = null;

    // Nullstill alt som er avhengig av fil
    allMappings.value = [];
    selectedImportActivities.value = [];
    simulationResult.value = null;
    isSimulating.value = false;
    analyse.value = null;
    analyseError.value = null;
    analyseLines.value = 100;
    isImporting.value = false;
    importResult.value = null;
  }

  async function resetWizard () {
    currentStep.value = 1;
    selectedSchema.value = '';
    selectedTableDefinitionId.value = null;
    selectedTableName.value = '';
    currentTable.value = null;
    useExistingTemplate.value = null;
    selectedTemplate.value = null;
    newTemplateName.value = '';
    newTemplateDescription.value = '';
    importTemplates.value = [];
    eventStreamLogs.value = [];
    // Kall kun fil-reset én gang
    resetFileOnly();
  }

  const isFetchingPreview = ref(false);
  const previewError = ref<string | null>(null);

  async function fetchPreviewForCurrentFile (uploadId?: string) {
    const targetUploadId = uploadId || activeUploadId.value;
    if (!targetUploadId) {
      return;
    }

    isFetchingPreview.value = true;
    previewError.value = null;
    try {
      await filePreviewStore.getFilePreview(targetUploadId);

      // Hent fil-data for å sjekke om det er Excel
      const file = getFileByUploadId(targetUploadId);
      if (file?.isExcel) {
        const sheetIds = Object.values(file?.sheets || {});
        if (sheetIds.length > 0) {
          await fileInputChanged(String(sheetIds[0]));
        }
      } else {
        await fileInputChanged(targetUploadId);
      }
      populatePreviewData();
      // SJEKK: Hvis previewRows er tom, sett error!
      if (!previewRows.value || previewRows.value.length === 0) {
        previewError.value = 'Ingen rader i forhåndsvisning av filen. Kontroller filen og prøv igjen.';
        console.error('[Wizard] Inkonsistent state: previewRows er tom etter fetch, setter previewError!');
      }
      isFetchingPreview.value = false;
    } catch (err: any) {
      isFetchingPreview.value = false;
      previewError.value = 'Klarte ikke å hente forhåndsvisning av filen';
      console.error('[Wizard] Feil ved henting av forhåndsvisning:', err);
    }
  }

  function populatePreviewData () {
    console.log('[Wizard] populatePreviewData:', preview.value);
    if (preview.value?.headerRow?.length > 0) {
      previewColumns.value = preview.value.headerRow;
    } else {
      previewColumns.value = [];
    }
    previewRows.value = preview.value?.previewRows || [];
  }

  function clearPreview () {
    filePreviewStore.clearPreview();
    previewColumns.value = [];
    previewRows.value = [];
  }

  // NY: Synkroniser fil-status når SignalR-tilkoblingen etableres på nytt
  async function syncFileStatusOnConnection () {
    // Sjekk om det er aktive uploads som venter
    if (!activeUploadId.value || !isUploadProcessing.value) {
      return;
    }

    console.log('[Wizard] Synkroniserer fil-status for uploadId:', activeUploadId.value);

    try {
      // Hent fil-status via API
      await uploadStore.fetchFileStatus(activeUploadId.value);

      // Hent fil-data fra uploadStore
      const file = getFileByUploadId(activeUploadId.value);

      if (file) {
        // Sjekk om filen er ferdig behandlet
        // Filen er ferdig hvis den ikke er processing og ikke har feil, eller hvis den har processedDate
        const isComplete = !file.isProcessing && !file.isFailed && (file.processedDate || file.isReadyForImport);

        if (isComplete) {
          console.log('[Wizard] Fil er ferdig behandlet, henter preview...');
          isUploadingFile.value = false;
          await fetchPreviewForCurrentFile();
          isUploadProcessing.value = false;
        } else if (file.isFailed) {
          console.log('[Wizard] Fil har feilet');
          isUploadingFile.value = false;
          isUploadProcessing.value = false;
          previewError.value = file.error || 'Filen kunne ikke behandles';
        }
        // Hvis filen fortsatt behandles, vent på SignalR-meldinger
      }
    } catch (error) {
      console.error('[Wizard] Feil ved synkronisering av fil-status:', error);
      // Ikke sett isUploadProcessing til false her - la SignalR-meldinger håndtere det
    }
  }

  // NY: Håndter ReceiveUploadProgress callback
  async function handleUploadProgress (file: IcsBlobItemResponse) {
    // Kun håndter hvis dette er den aktive uploaden
    if (file.uploadId !== activeUploadId.value) {
      return;
    }

    // Filen er ferdig behandlet
    if (file.processStatus === ProcessStatus.ready || file.processStatus === ProcessStatus.completed) {
      console.log('[Wizard] Fil ferdig behandlet via ReceiveUploadProgress, henter preview...');
      isUploadingFile.value = false;
      await fetchPreviewForCurrentFile(file.uploadId);
      isUploadProcessing.value = false;
    } else if (file.isFailed || file.processStatus === ProcessStatus.failed) {
      console.log('[Wizard] Fil har feilet via ReceiveUploadProgress');
      isUploadingFile.value = false;
      isUploadProcessing.value = false;
      previewError.value = file.error || 'Filen kunne ikke behandles';
    }
  }

  async function handleProgressUpdate (progress: ProgressResponse) {
    if (progress.processType !== ProcessType.simulering && progress.processType !== ProcessType.import) {
      return;
    }

    const event: EventStreamUpdate = {
      dbName: progress.dbName,
      fileId: progress.uploadId,
      jobId: progress.importId,
      processType: progress.processType,
      processStatus: progress.processStatus,
      message: progress.message,
      timestamp: progress.timestamp || new Date().toISOString(),
      hasError: progress.hasError,
      taskName: progress.taskName,
      fileName: progress.fileName,
      isComplete: progress.isComplete,
    };

    await handleEventStreamUpdate(event);
  }

  // NY: Håndter EventStreamUpdate callback
  async function handleEventStreamUpdate (eventStreamUpdate: EventStreamUpdate) {
    // Legg til i logg
    eventStreamLogs.value.push(eventStreamUpdate);
    // Oppdater progress for denne filen
    if (eventStreamUpdate.fileId) {
      eventStreamProgress.value[eventStreamUpdate.fileId] = eventStreamUpdate;
    }

    // Hooks basert på processType
    switch (eventStreamUpdate.processType) {
      case ProcessType.simulering:
      case ProcessType.import:
      case ProcessType.upload:
      case ProcessType.validation:
      case ProcessType.export:
        // Ingen handling for processTypes pr nå, bare logge warnings hvis ukjente typer
        break;
      default:
        console.warn('[Wizard] Ukjent processType:', eventStreamUpdate.processType);
    }

    // Hooks basert på processStatus
    switch (eventStreamUpdate.processStatus) {
      case ProcessStatus.completed:
        await handleCompletedStatus(eventStreamUpdate);
        break;
      case ProcessStatus.failed:
        handleFailedStatus(eventStreamUpdate);
        break;
      case ProcessStatus.timeout:
        handleTimeoutStatus(eventStreamUpdate);
        break;
      case ProcessStatus.pending:
      case ProcessStatus.processing:
        // Ingen handling for pending og processing pr nå
        break;
      default:
        console.warn('[Wizard] Ukjent processStatus:', eventStreamUpdate.processStatus);
    }
  }

  // Hjelpefunksjon for å nullstille loading states basert på processType
  function resetLoadingStates (event: EventStreamUpdate) {
    if (event.processType === ProcessType.simulering && event.fileId === selectedUploadId.value) {
      isSimulating.value = false;
    }
    if (event.processType === ProcessType.import && event.fileId === selectedUploadId.value) {
      isImporting.value = false;
    }
    if (event.processType === ProcessType.upload && event.fileId === activeUploadId.value) {
      isUploadingFile.value = false;
      isUploadProcessing.value = false;
    }
  }

  // Hooks for spesifikke processTypes

  async function handleCompletedStatus (event: EventStreamUpdate) {
    if (event.processType === ProcessType.simulering && event.fileId === selectedUploadId.value) {
      console.log('[Wizard] Simulering ferdig, henter resultat...');

      const result = await fetchResult(event.jobId);
      simulationResult.value = result?.result;
      notificationsStore.addSuccess('Simulering fullført');
      console.log('[Wizard] Simulering resultat:', simulationResult.value);
      isSimulating.value = false;
    }

    if (event.processType === ProcessType.import && event.fileId === selectedUploadId.value) {
      console.log('[Wizard] Import ferdig, henter resultat...');

      importResult.value = await fetchResult(event.jobId);
      console.log('[Wizard] Import resultat:', importResult.value);
      notificationsStore.addSuccess('Import fullført');
      isImporting.value = false;
    }
    if (event.processType === ProcessType.upload && event.isComplete && event.fileId === activeUploadId.value) {
      console.log('[Wizard] Upload ferdig, isUploadProcessing:', isUploadProcessing.value, event, ', fetchPreviewForCurrentFile');
      isUploadingFile.value = false;
      await fetchPreviewForCurrentFile(event.fileId);
      isUploadProcessing.value = false;
    }
    if (event.hasError) {
      console.warn('[Wizard] Completed with errors:', event.message);
      resetLoadingStates(event);
    }
  }

  async function handleFailedStatus (event: EventStreamUpdate) {
    console.error('[Wizard] Failed status for:', event.processType, event.taskName, event.message);

    resetLoadingStates(event);

    // Hvis det er en import-feil, hent import-resultatet for å vise detaljer
    if (event.processType === ProcessType.import && event.fileId === selectedUploadId.value && event.jobId) {
      console.log('[Wizard] Import feilet, henter import-resultat for jobId:', event.jobId);
      importResult.value = await fetchResult(event.jobId);
    }

    // Hvis det er en simulering-feil, hent simulering-resultatet for å vise detaljer
    if (event.processType === ProcessType.simulering && event.fileId === selectedUploadId.value && event.jobId) {
      console.log('[Wizard] Simulering feilet, henter simulering-resultat for jobId:', event.jobId);
      const result = await fetchResult(event.jobId);
      simulationResult.value = result?.result;
    }

    // Vis feilmelding til bruker
    notificationsStore.addError(
      `${event.taskName || event.processType} feilet`,
      event.message || 'Ukjent feil',
    );
  }

  function handleTimeoutStatus (event: EventStreamUpdate) {
    console.error('[Wizard] Timeout status for:', event.processType, event.taskName);

    resetLoadingStates(event);

    // Vis timeout-melding til bruker
    notificationsStore.addError(
      `${event.taskName || event.processType} timeout`,
      'Prosesseringen tok for lang tid',
    );
  }

  const selectedFileProgress = computed(() => {
    const uploadId = selectedUploadId.value ?? activeUploadId.value;
    if (!uploadId) return null;
    const progress = eventStreamProgress.value[uploadId] || null;
    // Ekstra sjekk for inkonsistent state mellom upload og første event
    if (!isUploadProcessing.value && !previewError.value && previewRows.value.length === 0 && !isFetchingPreview.value) {
      console.error('[Wizard] Inkonsistent state: Ingen error, tom previewRows, ikke fetching! Dette skal ikke skje.');
    }
    return progress;
  });

  const uploadedFileProgress = computed(() => {
    const uploadId = activeUploadId.value;
    if (!uploadId) return null;
    const progress = eventStreamProgress.value[uploadId] || null;
    // Ekstra sjekk for inkonsistent state mellom upload og første event
    if (!isUploadProcessing.value && !previewError.value && previewRows.value.length === 0 && !isFetchingPreview.value) {
      console.error('[Wizard] Inkonsistent state: Ingen error, tom previewRows, ikke fetching! Dette skal ikke skje.');
    }
    return progress;
  });

  const stepConfigs: WizardStepConfig[] = [
    {
      title: 'Velg tabell',
      description: 'Velg skjema og tabell for import',
      canProceed: () => !!selectedTableDefinitionId.value,
      label: 'Neste',
      onClick: nextStep,
    },
    {
      title: 'Importmetode',
      description: 'Velg importmetode',
      canProceed: () => useExistingTemplate.value !== null &&
        (useExistingTemplate.value === 'manual' ||
          (useExistingTemplate.value === 'existing' && !!selectedTemplate.value) ||
          (useExistingTemplate.value === 'new' && !!newTemplateName.value.trim() && !!newTemplateDescription.value.trim())),
      label: 'Neste',
      onClick: nextStep,
    },
    {
      title: 'Velg fil',
      description: 'Last opp fil for import',
      canProceed: () => !!activeUploadId.value && !isUploadProcessing.value,
      label: 'Analyser fil',
      onClick: executeAnalyze,
    },
    {
      title: 'Kolonne-mapping',
      description: 'Definer mapping for kolonner',
      canProceed: () => !isAnalyzing.value,
      label: 'Neste',
      onClick: handleColumnMappingNext,
    },
    {
      title: 'Oppdatering',
      description: 'Velg hva som skal oppdateres',
      canProceed: () => selectedValueMappings.value.length > 0,
      label: 'Simuler import',
      onClick: runSimulation,
    },
    {
      title: 'Simulering',
      description: 'Kjør simulering før import',
      canProceed: () => !isSimulating.value && !!simulationResult.value && selectedImportActivities.value.length > 0,
      label: 'Utfør import',
      onClick: executeImport,
      // NY: Spesialhåndtering for step 6 - vis forskjellige knapper basert på om det er mal eller ikke
      isTemplateStep: true,
    },
    {
      title: 'Ferdig',
      description: 'Import resultat og oppsummering',
      canProceed: () => !isImporting.value && !!importResult.value,
      label: 'Start ny import',
      onClick: resetWizard,
      icon: 'home',
    },
  ];

  const isStepAvailable = (stepIndex: number): boolean => {
    if (stepIndex === 0) return true; // Første steg er alltid tilgjengelig

    // Et steg er tilgjengelig hvis vi har vært på det (currentStep > stepIndex)
    // eller hvis vi kan gå videre fra forrige steg (canProceed for forrige steg)
    return currentStep.value > stepIndex ||
      (currentStep.value === stepIndex &&
        stepConfigs[stepIndex - 1]?.canProceed());
  };

  const navigateToStep = (targetStep: number) => {
    const currentStepIndex = currentStep.value - 1;
    const targetStepIndex = targetStep - 1;

    // Hvis vi allerede er på målet eller skal gå bakover, bare naviger
    if (targetStepIndex <= currentStepIndex) {
      goToStep(targetStep);
      return;
    }

    // Hvis vi skal gå framover, må vi utføre handlingene for alle mellomliggende steg
    // For enkelhets skyld, la oss kjøre onClick for steget rett før målsteget
    if (targetStepIndex > 0 && stepConfigs[targetStepIndex - 1]) {
      // Sjekk om vi kan gå videre fra steget før målet
      if (stepConfigs[targetStepIndex - 1].canProceed()) {
        // Utfør onClick-handlingen for steget før målet
        stepConfigs[targetStepIndex - 1].onClick();
      } else {
        console.warn('Kan ikke navigere til steg', targetStep, 'fordi forrige steg ikke er fullført');
        // Eventuelt vis en melding til brukeren
      }
    }
  };

  const currentStepConfig = computed(() => stepConfigs[(currentStep.value - 1) % stepConfigs.length]);

  const overlayMessage = computed(() => {
    if (isAnalyzing.value) {
      return uploadedFileProgress.value?.message || 'Analyserer filen, vennligst vent...';
    }
    if (isSimulating.value) {
      return selectedFileProgress.value?.message || 'Simulerer import, vennligst vent...';
    }
    if (isImporting.value) {
      return selectedFileProgress.value?.message || 'Utfører import, vennligst vent...';
    }
    return 'Vennligst vent...';
  });

  // NY: Validering-funksjoner for mapping
  const validateMappings = () => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Sjekk om vi har minst én verdi eller kommentar mapping
    if (valueMappings.value.length === 0 && commentMappings.value.length === 0) {
      errors.push('Du må ha minst én verdi eller kommentar mapping for å fortsette');
    }

    // Sjekk om vi har nøkkel mapping hvis vi har kommentar mapping
    if (commentMappings.value.length > 0 && keyMappings.value.length === 0) {
      errors.push('Du må ha minst én nøkkel mapping når du har kommentar mapping');
    }

    // Sjekk ufullstendige mappinger - disse er FEIL, ikke advarsler
    const incompleteKeyMappings = keyMappings.value.filter(m => !m.fileColumn || !m.tableColumn);
    const incompleteValueMappings = valueMappings.value.filter(m => !m.fileColumn || !m.tableColumn);
    const incompleteCommentMappings = commentMappings.value.filter(m => !m.fileColumn || !m.tableColumn);

    if (incompleteKeyMappings.length > 0) {
      errors.push(`${incompleteKeyMappings.length} nøkkel mapping(er) er ikke fullstendig utfylt`);
    }

    if (incompleteValueMappings.length > 0) {
      errors.push(`${incompleteValueMappings.length} verdi mapping(er) er ikke fullstendig utfylt`);
    }

    if (incompleteCommentMappings.length > 0) {
      errors.push(`${incompleteCommentMappings.length} kommentar mapping(er) er ikke fullstendig utfylt`);
    }

    return { errors, warnings, isValid: errors.length === 0 };
  };

  const getIncompleteMappings = () => {
    return {
      keys: keyMappings.value.filter(m => !m.fileColumn || !m.tableColumn),
      values: valueMappings.value.filter(m => !m.fileColumn || !m.tableColumn),
      comments: commentMappings.value.filter(m => !m.fileColumn || !m.tableColumn)
    };
  };

  return {
    // State
    isLoadingSchemas,
    isLoadingTables,
    isLoadingTemplates,
    isSimulating,
    isAnalyzing,
    isImporting,
    isUploadingFile,
    isUploadProcessing,
    isWorking,

    currentStep,
    totalSteps,
    selectedSchema,
    selectedTableDefinitionId,
    selectedTableName,
    selectedUploadId,
    schemas,
    tables,
    useExistingTemplate,
    selectedTemplate,
    newTemplateName,
    newTemplateDescription,
    importTemplates,
    keyMappings,
    valueMappings,
    selectedValueMappings,
    simulationResult,
    headerRowIndex,
    firstDataRowIndex,
    previewLines,
    analyse,
    analyseLines,
    analyseError,
    uploadProgress,
    allMappings,
    selectableMappings,
    selectedMappings,
    selectedAndKeyMappings,

    // Computed
    currentTable,
    currentTemplate,
    uploadedFile,
    sheetName,
    importResult,
    previewColumns,
    previewRows,
    commentMappings,
    selectedCommentMappings,
    dateFormatOptions,
    selectedImportActivities,
    emailReceipt,
    eventStreamLogs,
    eventStreamProgress,
    selectedFileProgress,
    uploadedFileProgress,

    // Actions
    fetchSchemas,
    setSchema,
    setTable,
    setTemplateChoice,
    setSelectedTemplate,
    processFile,
    runSimulation,
    executeImport,
    saveTemplateAndExit,
    saveTemplateAndImport,
    nextStep,
    handleColumnMappingNext,
    previousStep,
    goToStep,
    resetWizard,
    resetFileOnly,
    clearPreview,
    fileInputChanged,
    toggleMappingSelection,
    addValueMapping,
    removeValueMapping,
    addKeyMapping,
    removeKeyMapping,
    getFileByUploadId,
    getFilesByExtractedFromUploadId,
    getSimulationCountByName,
    getRelevantActivitiesForSimulation,
    autoSelectActivities,
    toggleImportActivity,
    isImportActivitySelected,
    isMappingSelected,
    isDateColumn,
    isCommentColumn,
    isKeyColumn,
    getColumnDataType,
    addCommentMapping,
    removeCommentMapping,
    updateMapping,
    isFetchingPreview,
    previewError,
    sendEmailReportForImport,
    handleEventStreamUpdate,
    handleProgressUpdate,
    syncFileStatusOnConnection,
    handleUploadProgress,
    stepConfigs,
    currentStepConfig,
    overlayMessage,
    isStepAvailable,
    navigateToStep,
    validateMappings,
    getIncompleteMappings
  };
});

